<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition name="fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-modal"
        style="background: rgba(18, 23, 26, 0.4); backdrop-filter: blur(2px);"
        @click="onBackdropClick"
      />
    </Transition>

    <!-- Sheet -->
    <Transition name="sheet-slide-up">
      <div
        v-if="modelValue"
        ref="panelRef"
        tabindex="-1"
        class="fixed bottom-0 left-0 right-0 z-modal-panel overflow-y-auto"
        style="background: var(--surface); border-radius: 8px 8px 0 0; box-shadow: var(--shadow-2xl); max-height: 85vh; max-height: 85dvh; overscroll-behavior: contain;"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel || title"
      >
        <!-- Grab handle — a real button so the dismiss target is finger-sized
             (the global button rule guarantees a 44px hit area) -->
        <button
          v-if="showHandle"
          type="button"
          class="w-full flex items-center justify-center py-2"
          style="background: none; border: none; cursor: pointer;"
          :aria-label="$t('common.close')"
          @click="dismiss"
        >
          <span
            style="width: 40px; height: 4px; background: var(--border-default); border-radius: 2px;"
            aria-hidden="true"
          />
        </button>

        <!-- Title row -->
        <slot name="header">
          <div v-if="title" class="px-6 py-4 pb-2" :class="showClose ? 'flex justify-between items-center' : ''">
            <div :class="centerTitle ? 'w-full text-center' : ''">
              <h3 class="m-0" style="font-size: var(--text-xl); color: var(--text-primary);">{{ title }}</h3>
              <p v-if="subtitle" class="mt-1 m-0" style="font-size: var(--text-sm); color: var(--text-tertiary);">
                {{ subtitle }}
              </p>
            </div>
            <button
              v-if="showClose"
              type="button"
              class="flex items-center justify-center w-8 h-8 shrink-0"
              style="background: transparent; border: none; cursor: pointer; color: var(--text-tertiary);"
              :aria-label="$t('common.close')"
              @click="dismiss"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </slot>

        <!-- Content. Bottom padding covers the iOS home-indicator inset so
             the last row stays tappable on notched devices. -->
        <div
          :class="contentPadding ? 'px-6 py-4' : ''"
          style="padding-bottom: calc(2rem + var(--safe-bottom));"
        >
          <slot />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, ref, toRef, watch } from "vue"
import { useBodyScrollLock } from "../../composables/useBodyScrollLock"
import { useFocusTrap } from "../../composables/useFocusTrap"
import { useModalBackClose } from "../../composables/useModalBackClose"

/**
 * The one bottom-sheet primitive. Same guarantees as AppModal — Teleport to
 * <body>, --z-modal layer, backdrop, focus trap, Escape / backdrop-click /
 * Android-back dismissal, ref-counted scroll lock — in sheet form.
 */
interface Props {
  modelValue: boolean
  title?: string
  subtitle?: string
  /** Accessible name when there is no title. */
  ariaLabel?: string
  showHandle?: boolean
  /** X button in the title row. */
  showClose?: boolean
  centerTitle?: boolean
  /** Standard px-6 py-4 around the slot; disable for edge-to-edge content. */
  contentPadding?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showHandle: true,
  showClose: true,
  centerTitle: false,
  contentPadding: true,
})

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void
  (e: "close"): void
}>()

const panelRef = ref<HTMLDivElement | null>(null)

function dismiss() {
  emit("close")
  emit("update:modelValue", false)
}

function onBackdropClick() {
  dismiss()
}

// Tab stays inside the sheet; Escape closes; focus returns to the opener.
useFocusTrap(panelRef, toRef(props, "modelValue"), { onEscape: dismiss })

// Android hardware back closes the sheet instead of leaving the screen
useModalBackClose(() => props.modelValue, dismiss)

// Ref-counted body scroll lock (shared with every other overlay)
useBodyScrollLock(() => props.modelValue)

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) nextTick(() => panelRef.value?.focus({ preventScroll: true }))
  },
)
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.sheet-slide-up-enter-active,
.sheet-slide-up-leave-active {
  transition: transform 0.3s ease;
}

.sheet-slide-up-enter-from,
.sheet-slide-up-leave-to {
  transform: translateY(100%);
}
</style>
